import { addDays, deviceTimeZone, minutesOfDay, todayKey, type DateKey } from './date.js';
import { PRAYERS, type PrayerId } from './prayer.js';
import type { TimeZoneName } from './date.js';

/** 'HH:MM', 24-hour, on the wall clock of the zone the times were worked out for. */
export type TimeOfDay = string;

/** The five prayers' call times. */
export type PrayerTimeSet = Record<PrayerId, TimeOfDay>;

/** A day's times: the five calls, and sunrise (when Fajr ends). */
export type DayTimes = PrayerTimeSet & { sunrise: TimeOfDay };

export interface PrayerTimesProvider {
  readonly id: string;
  /** The zone the times are read in. */
  readonly timeZone: TimeZoneName;
  forDate(date: DateKey): DayTimes;
}

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export const CALCULATION_METHODS = [
  'MuslimWorldLeague',
  'Egyptian',
  'Karachi',
  'UmmAlQura',
  'Dubai',
  'NorthAmerica',
  'Turkey',
  'MoonsightingCommittee',
] as const;
export type CalculationMethodName = (typeof CALCULATION_METHODS)[number];

export const MADHABS = ['shafi', 'hanafi'] as const;
/** Which madhab's rule for the length of the ‘Asr shadow. */
export type MadhabName = (typeof MADHABS)[number];

export interface TimesSettings {
  coordinates: Coordinates | null;
  method: CalculationMethodName;
  madhab: MadhabName;
}

export const DEFAULT_TIMES_SETTINGS: TimesSettings = {
  coordinates: null,
  method: 'MuslimWorldLeague',
  madhab: 'shafi',
};

export function isCoordinates(value: unknown): value is Coordinates {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.latitude === 'number' &&
    typeof v.longitude === 'number' &&
    Number.isFinite(v.latitude) &&
    Number.isFinite(v.longitude) &&
    Math.abs(v.latitude) <= 90 &&
    Math.abs(v.longitude) <= 180
  );
}

/** The sample day used where nothing real can be worked out. */
const PLACEHOLDER: DayTimes = {
  fajr: '05:12',
  sunrise: '06:40',
  dhuhr: '13:04',
  asr: '16:37',
  maghrib: '19:48',
  isha: '21:15',
};

/**
 * The fallback used until someone sets a location. Real times need coordinates,
 * and asking a device for them is a decision the person using the app has to
 * make — so until they do, these stand in and the app says so plainly.
 */
export function placeholderTimesFor(
  timeZone: TimeZoneName = deviceTimeZone(),
): PrayerTimesProvider {
  return {
    id: 'placeholder',
    timeZone,
    forDate: () => ({ ...PLACEHOLDER }),
  };
}

export const placeholderTimes: PrayerTimesProvider = {
  id: 'placeholder',
  get timeZone() {
    return deviceTimeZone();
  },
  forDate: () => ({ ...PLACEHOLDER }),
};

export function minutes(time: TimeOfDay): number {
  const [h, m] = time.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/** Minutes since midnight as 'HH:MM', wrapping past midnight in either direction. */
export function toTimeOfDay(total: number): TimeOfDay {
  const wrapped = ((Math.round(total) % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  return `${`${h}`.padStart(2, '0')}:${`${wrapped % 60}`.padStart(2, '0')}`;
}

/**
 * The five prayers belong to a day that runs from one Fajr to the next, not
 * from one midnight to the next.
 *
 * It matters far enough north that ‘Ishā' falls after midnight: on the
 * calendar it belongs to a date the device has already turned over from, so it
 * was recorded against the wrong day and left the day it belonged to
 * permanently incomplete. Measuring everything from Fajr also puts the five in
 * the order the screen already lists them.
 */
export function sinceFajr(time: TimeOfDay, fajr: TimeOfDay): number {
  return (minutes(time) - minutes(fajr) + 1440) % 1440;
}

/** How far into the current prayer-day we are, in minutes. */
export function elapsedSinceFajr(
  fajr: TimeOfDay,
  now: Date = new Date(),
  timeZone: TimeZoneName = deviceTimeZone(),
): number {
  return (minutesOfDay(now, timeZone) - minutes(fajr) + 1440) % 1440;
}

/**
 * The day a prayer recorded right now belongs to: today once Fajr has come in,
 * and yesterday until it does.
 */
export function recordDay(
  fajr: TimeOfDay,
  now: Date = new Date(),
  timeZone: TimeZoneName = deviceTimeZone(),
): DateKey {
  const today = todayKey(now, timeZone);
  return minutesOfDay(now, timeZone) >= minutes(fajr) ? today : addDays(today, -1);
}

/** Has this prayer's time arrived? Drives "later today" vs "not recorded". */
export function isDue(
  time: TimeOfDay,
  fajr: TimeOfDay,
  now: Date = new Date(),
  timeZone: TimeZoneName = deviceTimeZone(),
): boolean {
  return elapsedSinceFajr(fajr, now, timeZone) >= sinceFajr(time, fajr);
}

/**
 * The prayer whose time we are in: the last one to have come in, measured
 * from Fajr so it wraps where the record day does.
 *
 * There is always exactly one — ‘Ishā' runs until the next Fajr — so this
 * returns a prayer rather than a maybe. Ordering by `sinceFajr` rather than by
 * position in PRAYERS means an ‘Ishā' past midnight, whose clock time reads as
 * earlier than Fajr's, still lands last where it belongs.
 */
export function currentPrayer(
  times: PrayerTimeSet,
  now: Date = new Date(),
  timeZone: TimeZoneName = deviceTimeZone(),
): PrayerId {
  const elapsed = elapsedSinceFajr(times.fajr, now, timeZone);
  let current: PrayerId = PRAYERS[0];
  let best = -1;
  for (const id of PRAYERS) {
    const at = sinceFajr(times[id], times.fajr);
    if (at <= elapsed && at >= best) {
      best = at;
      current = id;
    }
  }
  return current;
}
